import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import SvgIcon from "@mui/material/SvgIcon";
import Typography from "@mui/material/Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import useUserPreferencesStore from "@stores/shared/useUserPreferencesStore";

export const SearchConfigureButton = ({ semanticLevel, query, onSemanticChange, onApply }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingLevel, setPendingLevel] = useState(semanticLevel ?? 1);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    setPendingLevel(semanticLevel ?? 1);
  }, [semanticLevel]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    setApplied(false);
  };

  const handleClose = () => setAnchorEl(null);

  const handleApply = async () => {
    onSemanticChange(pendingLevel);
    try {
      const { setPreference } = useUserPreferencesStore.getState();
      await setPreference("search-semantic-weight", String(pendingLevel));
    } catch (err) {
      console.error("Error saving search preference:", err);
    }
    if (query.trim()) onApply();
    setApplied(true);
  };

  return (
    <>
      <Button
        size="small"
        variant="text"
        onClick={handleOpen}
        startIcon={
          <SvgIcon sx={{ fontSize: "0.85rem !important" }}>
            <FontAwesomeIcon icon={faSliders} />
          </SvgIcon>
        }
        sx={{
          color: (theme) => theme.palette.brandText,
          fontSize: "0.75rem",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {t("search.configureSearch")}
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { p: 2.5, width: 340 } } }}
      >
        <Stack spacing={2}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              {t("search.configureTitle")}
            </Typography>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{
                mt: -0.5,
                mr: -0.5,
                width: 20,
                height: 20,
                borderRadius: "50%",
                bgcolor: "action.selected",
                "&:hover": { bgcolor: "action.focus" },
              }}
            >
              <SvgIcon sx={{ fontSize: "0.7rem" }}>
                <FontAwesomeIcon icon={faXmark} />
              </SvgIcon>
            </IconButton>
          </Box>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography
                variant="caption"
                sx={{
                  color: pendingLevel === 1 ? "text.primary" : "text.disabled",
                  fontWeight: pendingLevel === 1 ? 600 : 400,
                  whiteSpace: "nowrap",
                  transition: "color 0.2s",
                }}
              >
                {t("search.keyword")}
              </Typography>
              <Slider
                value={pendingLevel}
                min={1}
                max={5}
                step={1}
                marks
                size="small"
                onChange={(_, v) => {
                  setPendingLevel(v);
                  setApplied(false);
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: pendingLevel === 5 ? "text.primary" : "text.disabled",
                  fontWeight: pendingLevel === 5 ? 600 : 400,
                  whiteSpace: "nowrap",
                  transition: "color 0.2s",
                }}
              >
                {t("search.semantic")}
              </Typography>
            </Stack>

            <Typography variant="caption" color="textSecondary">
              {t(`search.semanticLevel.${pendingLevel}`)}
            </Typography>
          </Stack>

          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={handleApply}
            sx={{ alignSelf: "flex-end" }}
          >
            {t("search.applyWeight")}
          </Button>

          {applied && (
            <Typography variant="caption" color="textSecondary" sx={{ fontStyle: "italic" }}>
              {t("search.preferenceCaption")}
            </Typography>
          )}
        </Stack>
      </Popover>
    </>
  );
};

SearchConfigureButton.propTypes = {
  semanticLevel: PropTypes.number,
  query: PropTypes.string.isRequired,
  onSemanticChange: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
};
